import { useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@shared/api/client';
import { Breadcrumb } from '@shared/chrome/Breadcrumb';
import { resolveErrorCopy } from '@shared/errors/catalog';
import { ConfirmDialog, type ConfirmSubject } from '@shared/ui/dialog';
import { EmptyState } from '@shared/ui/EmptyState';
import { ErrorBanner } from '@shared/ui/ErrorBanner';
import { PlusIcon } from '@shared/ui/icons';

import { categoryErrors } from '../_shared/errors';
import { TermDialog } from '../_shared/TermDialog';
import { flattenTree, parentIdOf, selfAndDescendantIds } from '../_shared/tree';
import type { CategoryNode, TermDeletion, TermDraft } from '../_shared/types';
import { ROOT_PARENT_VALUE } from '../_shared/forms.schema';
import { CategoriesSkeleton } from './Categories.Skeleton';
import { TermTree } from './Categories.TermTree';
import { copy } from './copy';
import { useCreateCategory, useDeleteCategory, useUpdateCategory } from './mutations';
import { categoriesKey, useCategoryTree } from './queries';

/**
 * Category-tree management — the genre vocabulary of the catalogue, at any
 * depth, with every destructive action confirmed by name before it runs.
 *
 * **The tree is the component, not a table.** Depth is what the catalogue asks
 * for and what a table cannot express without an invented indentation
 * convention; the sibling tag surface is a table for the mirror reason, because
 * its row says a tag is flat. One vocabulary surface each, shaped by what the
 * row says the entity is.
 *
 * What this page deliberately does not draw, and why: **no title count** per
 * category, because no row gives a category a size on this surface; **no slug
 * field**, because no row authors a slug and a field here would claim the
 * operator composes public URLs; **no sort control**, because a tree's order is
 * its structure and a sort would flatten it; **no move handles**, because the
 * catalogue calls the shelf list ordered and says nothing about order among
 * siblings here; **no merge or move-branch action**, because re-parenting is
 * what the dialog's parent field already is.
 *
 * One dialog serves the whole tree in three modes, and one confirmation serves
 * every node: a fourteen-node tree would otherwise carry fourteen
 * focus-trappable dialogs whose copy drifts row by row.
 */
export function CategoriesPage() {
  const query = useCategoryTree();
  const queryClient = useQueryClient();

  const createCategory = useCreateCategory();
  const updateCategory = useUpdateCategory();
  const deleteCategory = useDeleteCategory();

  const [draft, setDraft] = useState<TermDraft | null>(null);
  const [deletion, setDeletion] = useState<TermDeletion | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const [writeError, setWriteError] = useState<unknown>(null);

  /**
   * Where focus lands after a node leaves the document. Removing the element
   * that held focus would drop the reader to `<body>`, and the section's add
   * action is the one control on this surface that is always present — including
   * when the deletion emptied the tree and the empty state is what remains.
   */
  const addButtonRef = useRef<HTMLButtonElement>(null);

  // Memoised so the identity is stable between renders: the flatten below reads
  // it, and a fresh `[]` on every render would re-walk the tree on every
  // keystroke inside the dialog.
  const nodes = useMemo(() => query.data?.items ?? [], [query.data]);

  /** Full paths, depth-first — the list the parent field offers. */
  const flat = useMemo(() => flattenTree(nodes), [nodes]);

  const parentOptions = useMemo(() => {
    const forbidden = new Set(draft?.forbiddenParentIds ?? []);
    return flat
      .filter((node) => !forbidden.has(node.id))
      .map((node) => ({ value: node.id, label: node.path }));
  }, [flat, draft]);

  function openCreateRoot() {
    setWriteError(null);
    setDraft({ mode: 'create-root', termId: null, name: '', parentId: null, forbiddenParentIds: [] });
  }

  function openCreateChild(node: CategoryNode) {
    setWriteError(null);
    setDraft({
      mode: 'create-child',
      termId: null,
      name: '',
      // The pressed node is the parent — that is what «add a child» means, and
      // the field opens on it rather than on the root.
      parentId: node.id,
      forbiddenParentIds: [],
    });
  }

  function openEdit(node: CategoryNode) {
    setWriteError(null);
    setDraft({
      mode: 'edit',
      termId: node.id,
      name: node.name,
      // The real parent, not the root. Opening on «بلا أب» would make «save»
      // silently promote the node to the top level.
      parentId: parentIdOf(nodes, node.id),
      forbiddenParentIds: selfAndDescendantIds(nodes, node.id),
    });
  }

  function submitDraft(values: { name: string; parentId: string | null }) {
    if (!draft) return;
    setWriteError(null);

    const settle = (announce: string) => {
      setDraft(null);
      setAnnouncement(announce);
    };

    if (draft.mode === 'edit' && draft.termId) {
      updateCategory.mutate(
        { categoryId: draft.termId, name: values.name, parentId: values.parentId },
        { onSuccess: () => settle(copy.announce.updated(values.name)), onError: setWriteError },
      );
      return;
    }

    createCategory.mutate(
      { name: values.name, parentId: values.parentId },
      { onSuccess: () => settle(copy.announce.created(values.name)), onError: setWriteError },
    );
  }

  /**
   * The delete runs from here and from nowhere else — `confirm=true` is set
   * inside the mutation, and this accept handler is its only caller.
   */
  function confirmDeletion() {
    if (!deletion) return;
    const { termId, name } = deletion;
    setWriteError(null);

    deleteCategory.mutate(termId, {
      onSuccess: () => {
        setAnnouncement(copy.announce.deleted(name));
        addButtonRef.current?.focus();
      },
      onError: setWriteError,
    });

    setDeletion(null);
  }

  const dialogTitle =
    draft?.mode === 'edit'
      ? copy.dialog.titleEdit
      : draft?.mode === 'create-child'
        ? copy.dialog.titleCreateChild
        : copy.dialog.titleCreateRoot;

  const confirmSubject: ConfirmSubject | null = deletion
    ? {
        heading: copy.confirm.heading(deletion.name),
        body: copy.confirm.body,
        commitLabel: copy.confirm.commit,
        intent: 'danger',
      }
    : null;

  const busy = createCategory.isPending || updateCategory.isPending;

  return (
    <>
      <Breadcrumb items={[{ label: copy.crumbs.root, to: '/' }, { label: copy.crumbs.current }]} />

      <div className="mb-6 mt-3 grid gap-stack-tight">
        <h1 className="font-display text-h1 font-bold">{copy.head.title}</h1>
        <p className="mb-0 max-w-measure text-[1.0625rem] text-ink-muted">{copy.head.lede}</p>
      </div>

      {/* One polite region for the whole surface, never one per control. */}
      <p role="status" aria-live="polite" className="sr-only">
        {announcement}
      </p>

      <section aria-labelledby="taxonomy-tree">
        <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-2">
          <h2 id="taxonomy-tree" className="font-display text-h2 font-bold">
            {copy.section.heading}
          </h2>
          {/*
            Secondary, like every committing action inside the content column:
            the top bar's create action is this view's one primary button.
          */}
          <button
            ref={addButtonRef}
            type="button"
            onClick={openCreateRoot}
            className="ms-auto flex min-h-6 items-center gap-2 rounded-sm border border-border-strong bg-surface-raised px-3 text-xs font-semibold text-ink hover:bg-surface-overlay"
          >
            <PlusIcon className="flex-none" />
            <span>{copy.section.add}</span>
          </button>
        </div>

        <p className="mb-4 mt-3 max-w-measure text-xs text-ink-muted">{copy.section.context}</p>

        {writeError ? <WriteError error={writeError} /> : null}

        {query.isError ? (
          <ReadError
            error={query.error}
            onRetry={() => void queryClient.invalidateQueries({ queryKey: categoriesKey() })}
          />
        ) : null}

        {query.isPending ? <CategoriesSkeleton /> : null}

        {query.data ? (
          nodes.length === 0 ? (
            <EmptyState message={copy.states.empty} />
          ) : (
            <div className="overflow-hidden rounded-md border border-border bg-surface">
              <div className="p-4">
                <TermTree
                  nodes={nodes}
                  onEdit={openEdit}
                  onAddChild={openCreateChild}
                  onDelete={(node) => setDeletion({ termId: node.id, name: node.name })}
                />
              </div>
            </div>
          )
        ) : null}
      </section>

      <TermDialog
        open={draft !== null}
        title={dialogTitle}
        context={copy.dialog.context}
        nameLabel={copy.dialog.nameLabel}
        saveLabel={copy.dialog.save}
        messages={copy.validation}
        defaultName={draft?.name ?? ''}
        defaultParentId={draft?.parentId ?? ROOT_PARENT_VALUE}
        parent={{
          label: copy.dialog.parentLabel,
          hint: copy.dialog.parentHint,
          rootOptionLabel: copy.dialog.parentRoot,
          options: parentOptions,
        }}
        busy={busy}
        onSubmit={submitDraft}
        onClose={() => setDraft(null)}
      />

      <ConfirmDialog
        open={deletion !== null}
        subject={confirmSubject}
        busy={deleteCategory.isPending}
        onConfirm={confirmDeletion}
        onCancel={() => setDeletion(null)}
      />
    </>
  );
}

/** A read that failed takes the section down; a retry re-runs it. */
function ReadError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const resolved = resolve(error);

  return <ErrorBanner message={resolved.message} tone={resolved.tone} onRetry={onRetry} />;
}

/**
 * A write that failed leaves the tree on screen and states what happened above
 * it. The banner carries no retry: a repeat of a write whose outcome is unknown
 * is the operator's decision, and the dialog is where they take it again.
 */
function WriteError({ error }: { error: unknown }) {
  const resolved = resolve(error);

  return (
    <div className="mb-4">
      <ErrorBanner message={resolved.message} tone={resolved.tone} />
    </div>
  );
}

function resolve(error: unknown) {
  const status = error instanceof ApiError ? error.status : 0;
  const code = error instanceof ApiError ? error.code : undefined;
  const copyForCode = resolveErrorCopy(code, status, categoryErrors);

  return {
    message: copyForCode.message,
    tone: copyForCode.tone === 'danger' ? ('danger' as const) : ('neutral' as const),
  };
}
